import { useState, useEffect, useCallback } from 'react';
import { useShop } from '../context/ShopContext';

interface FetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
}

/**
 * Declare the global window.shopify object from App Bridge 4.x
 */
declare global {
  interface Window {
    shopify?: {
      idToken: () => Promise<string>;
      resourcePicker: (options: {
        type: 'product' | 'collection' | 'variant';
        action?: 'select' | 'add';
        multiple?: boolean | number;
        filter?: { hidden?: boolean; variants?: boolean; draft?: boolean; archived?: boolean };
        selectionIds?: Array<{ id: string }>;
      }) => Promise<Array<{ id: string; title: string; handle: string; images?: Array<{ originalSrc: string }>; variants?: Array<{ id: string; title: string; price: string }> }> | undefined>;
      config: {
        apiKey: string;
        host: string;
      };
    };
  }
}

/**
 * Get session token from App Bridge (available in embedded apps).
 * Waits for App Bridge to initialize if needed.
 * Exported for use in components that need direct access to the token.
 */
export async function getSessionToken(): Promise<string | null> {
  // Wait for App Bridge to initialize (up to 1 second)
  let attempts = 0;
  while (!window.shopify && attempts < 10) {
    await new Promise(resolve => setTimeout(resolve, 100));
    attempts++;
  }

  if (!window.shopify) {
    console.warn('App Bridge (window.shopify) not available after waiting');
    return null;
  }

  if (typeof window.shopify.idToken !== 'function') {
    console.warn('window.shopify.idToken is not a function:', typeof window.shopify.idToken);
    return null;
  }

  try {
    const token = await window.shopify.idToken();
    return token;
  } catch (error) {
    console.error('Error getting session token:', error);
    return null;
  }
}

/**
 * Standalone authenticated fetch that returns raw Response.
 * Includes Bearer JWT token automatically.
 * For use in components that need raw Response handling.
 */
export async function apiFetch(endpoint: string, options: RequestInit = {}): Promise<Response> {
  const url = new URL(endpoint, window.location.origin);
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
    ...(options.headers as Record<string, string> || {}),
  };

  const sessionToken = await getSessionToken();
  if (sessionToken) {
    headers['Authorization'] = `Bearer ${sessionToken}`;
  }

  return fetch(url.toString(), {
    ...options,
    headers,
  });
}

export function useAuthenticatedFetch() {
  const fetchWithAuth = useCallback(
    async <T>(endpoint: string, options: FetchOptions = {}): Promise<T> => {
      const { method = 'GET', body } = options;
      
      const url = new URL(endpoint, window.location.origin);
      // Shop domain is extracted from the JWT session token on the backend
      // (ShopifySessionTokenMiddleware → HttpContext.Items["ShopifyShopDomain"]).
      // No need to send ?shop= as a query parameter.

      // Build headers with session token for authentication
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
      };

      // Get and include the session token if available
      const sessionToken = await getSessionToken();
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }

      const response = await fetch(url.toString(), {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(error.message || error.error || 'Request failed');
      }

      return response.json();
    },
    []
  );

  return fetchWithAuth;
}

// Hook for fetching recommendations
export function useRecommendations(productId?: number) {
  const [recommendations, setRecommendations] = useState<unknown[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchWithAuth = useAuthenticatedFetch();
  const { shop } = useShop();

  const fetchRecommendations = useCallback(async () => {
    if (!shop) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      let endpoint = '/api/recommendations';
      if (productId) {
        endpoint += `?productId=${productId}`;
      }
      
      const data = await fetchWithAuth<{ recommendations: unknown[] }>(endpoint);
      setRecommendations(data.recommendations || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch recommendations');
    } finally {
      setIsLoading(false);
    }
  }, [shop, productId, fetchWithAuth]);

  useEffect(() => {
    fetchRecommendations();
  }, [fetchRecommendations]);

  return { recommendations, isLoading, error, refresh: fetchRecommendations };
}

// Hook for fetching analytics
export function useAnalytics(days: number = 30) {
  const [analytics, setAnalytics] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchWithAuth = useAuthenticatedFetch();
  const { shop } = useShop();

  const fetchAnalytics = useCallback(async () => {
    if (!shop) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await fetchWithAuth(`/api/behavior/analytics?days=${days}`);
      setAnalytics(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch analytics');
    } finally {
      setIsLoading(false);
    }
  }, [shop, days, fetchWithAuth]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return { analytics, isLoading, error, refresh: fetchAnalytics };
}
